
import React, { useCallback, useEffect, useState } from 'react'
import { RunRowProps } from './RunTable.types'

import { useRuns } from '../hooks'
import RunDetails from '../RunDetails/RunDetails'

import './RunTable.scss'
import { FolderOutlined, InfoCircleOutlined, PartitionOutlined } from '@ant-design/icons'
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table'
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css'

interface HoverObject {
  // Over
  detailsOver: boolean
  workspaceOver: boolean
  debugOver: boolean
}

export const RunRow: React.FC<RunRowProps> = ({ 
  projectOwner,
  projectName,
  run,
  index,
  client,
  authUser,
  style,
  getFileCard,
  getFolderCard,
}) => {
  const { statusMap } = useRuns(client)

  const [open, setOpen] = useState<boolean>(false)

  /*
  * Manage hover 
  */
  const initialValues = { 
    detailsOver: false,
    workspaceOver: false,
    debugOver: false,
  } 

  const [hover, setHover] = useState<HoverObject>(initialValues)
  
  const toggleHover = useCallback((id: string, 
    value: boolean = false) => {
    setHover(prevHover => ({
      ...prevHover,
      [id]: value
    })
  )}, [hover])

  /**
   * Show extra inputs
   */
  const [extraInputs, setExtraInputs] = useState<any[]>([])

  useEffect(() => {
    if (!run || !index) return

    let recipeInputNames = [] as string[]
    if (run.recipe && run.recipe.inputs) {
      recipeInputNames = run.recipe.inputs.map(i => i.name)
    }
    const extraInputs = [
      {
        name: 'ID',
        value: index
      },
      { 
        name: 'steps',
        value: `${run.meta.progress.completed} / ${run.meta.progress.total}` 
      }, 
    ] as any[]
  
    if (run.status) {
      run.status.inputs.forEach(i => {
        if (!recipeInputNames.includes(i.name)) {
          extraInputs.push(i)
        }
      })
    }
  
    setExtraInputs(extraInputs)
  }, [run, index])

  return <div
      style={{
        ...style,
        margin: '10px 0 0 0',
        borderColor: statusMap[run.status.status]?.color 
          ?? '#d0d7de',
      }} 
      key={run.id}
      tabIndex={0}
      role='button'
      onClick={() => setOpen(p => !p)} 
      className={(!run.status.finished_at) 
        ? 'study-run-container blink-effect'
        : 'study-run-container'}>

      <div className='run-progress'>
      { extraInputs.length > 0 && 
        <Table>
          <Thead>
            <Tr>
              { extraInputs.map(({name}) => <Th key={name}>{name}</Th>) }
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              { extraInputs.map(({value}) => <Td key={value}>{value}</Td>) }
            </Tr>
          </Tbody>
        </Table> }
      </div>
      
      {/* Single run - use link logic */}
      {run &&
        <div className='run-link'>
          <span title='Go to run page'>
            <InfoCircleOutlined
              onClick={(e) => {
                e.stopPropagation()
                document.location = `/${projectOwner}/projects/${projectName}/studies/${run.status.job_id}/runs/${run.id}?tab=details`
              }}
              onMouseOver={(e) => {
                toggleHover('detailsOver', true)
              }}
              onMouseLeave={(e) => {
                toggleHover('detailsOver', false)
              }}
              style={{ marginRight: '0.75rem', 
                color: `${hover.detailsOver ? '#40a9ff' : '#000'}` }} />
          </span>
          <span title='Go to workspace page'>
            <FolderOutlined
              onClick={(e) => {
                e.stopPropagation()
                document.location = `/${projectOwner}/projects/${projectName}/studies/${run.status.job_id}/runs/${run.id}?tab=workspace&path=runs/${run.id}/workspace`
              }}
              onMouseOver={(e) => {
                toggleHover('workspaceOver', true)
              }}
              onMouseLeave={(e) => {
                toggleHover('workspaceOver', false)
              }}
              style={{ marginRight: '0.75rem', 
                color: `${hover.workspaceOver ? '#40a9ff' : '#000'}` }} />
          </span>
          <span title='Go to debug page'>
            <PartitionOutlined
              onClick={(e) => {
                e.stopPropagation()
                if (!run.status.finished_at) return
                
                document.location = `/${projectOwner}/projects/${projectName}/studies/${run.status.job_id}/runs/${run.id}?tab=debug`
              }}
              onMouseOver={(e) => {
                toggleHover('debugOver', true)
              }}
              onMouseLeave={(e) => {
                toggleHover('debugOver', false)
              }}
              style={{ color: `${(!run.status.finished_at) ? 'gray'
                  : hover.debugOver ? '#40a9ff' 
                  : '#000'}` }} />
          </span>
      </div>}

      {open && <div
        className='run-details'><RunDetails 
        projectOwner={projectOwner} 
        projectName={projectName}
        run={run}
        client={client}
        getFileCard={getFileCard}
        getFolderCard={getFolderCard}
        style={style}
      /></div>}
    </div>
}

export default RunRow

